// Call Sheet, the always-available pool game (CLAUDE.md: Core rules, Game screen
// order).
//
// Header, the line, the tiles, the input and Submit, the guesses counter, then
// the guess list — which is Call Sheet's alone, because a title guessed is not
// written anywhere else on the screen.
//
// Three guesses. Two actors to start and one more after each wrong guess, to a
// maximum of four. A repeated title is allowed: Submit stays live and nothing is
// blocked or warned about. What is not allowed is spending two guesses on one
// press, so a submission in flight ignores the next one.
//
// **Nothing about the answer is on this device until it is earned.** The server
// is asked again with every guess (cs_play) and answers with the actors revealed
// so far and, once the game is over, the answer. An unrevealed face is not in
// any response, so it cannot be in the page, the bundle or the network panel.
//
// A puzzle is played once and picked up where it was left, exactly as the daily
// is: the guesses are kept on this device (progress.ts) and the server is asked
// about them again on the way back in.
//
// **A replay is the one exception** (`/call-sheet/12?replay`): reached only from
// a played tile in the archive (owner, 16 October), it is a
// fresh board on a puzzle this device has already finished, and nothing about
// it is kept: no saved board, no result recorded, nothing towards stats. The
// finished game's own result stays exactly as it was. It is honoured only for a
// puzzle this device has finished; on any other it is an ordinary game.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GameHeader } from '../../components/GameHeader.tsx';
import { Notice } from '../../pages/Notice.tsx';
import { puzzleNumber } from '../../lib/puzzle-number.ts';
import { ActorTiles } from './ActorTiles.tsx';
import { TitleInput } from './TitleInput.tsx';
import { Result } from './Result.tsx';
import { useTitles } from './title-store.ts';
import { byId, kindLabel, type TitleRecord } from './titles.ts';
import { isSkip, loadProgress, saveProgress, type Guess } from './progress.ts';
import { latestPlayed, markPlayed, playedNumbers } from './played.ts';
import { useResultFromTop } from '../../lib/finish.ts';
import { pickNext } from '../../lib/next-puzzle.ts';
import { NAME_EM_FALLBACK, MAX_ACTORS, MAX_GUESSES, type CallSheetAnswer, type CallSheetPlay } from './types.ts';
import { PREVIEW } from '../../dev/preview.ts';
import { recordFinished } from '../../lib/history.ts';
import { wasWithdrawn } from '../../lib/archive.ts';
import { rpc } from '../../lib/api.ts';
import './CallSheet.css';

const play = (number: number, guesses: Guess[]) =>
  rpc<CallSheetPlay | null>('cs_play', { p_number: number, p_guesses: guesses });

type Loaded =
  | { state: 'loading' }
  | { state: 'none'; number: number | null; withdrawn?: boolean }
  | { state: 'error'; message: string }
  | { state: 'ready'; play: CallSheetPlay; guesses: Guess[] };

/**
 * /call-sheet chooses a puzzle and moves to its own address; /call-sheet/12
 * plays number 12. **Play takes a player back to a puzzle they left half-played,
 * then to the next one in pool order they have not finished.** With every
 * released Call Sheet finished it opens the result screen of the one finished
 * most recently, at that puzzle's own address (owner, 16 October); a new
 * release is offered again as soon as it exists.
 * The address then names the puzzle, so a refresh after the last guess returns
 * the result screen rather than serving the next one.
 */
export function CallSheetPage() {
  const params = useParams();
  const navigate = useNavigate();
  // No number: today's daily, or the next Call Sheet. A number that is not one —
  // "abc", "0", "-3", "2.5" — is an address that does not exist (lib/puzzle-number.ts).
  const wanted = params.number === undefined ? null : puzzleNumber(params.number);
  const badAddress = params.number !== undefined && wanted === null;
  const [search] = useSearchParams();
  const replay = search.has('replay') && wanted !== null && playedNumbers().includes(wanted);
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });

  useEffect(() => {
    if (badAddress) return;
    let live = true;
    setLoaded({ state: 'loading' });
    (async (): Promise<Loaded | null> => {
      if (wanted === null) {
        // This player's own next Call Sheet (lib/next-puzzle.ts); with every
        // released one finished, the one finished most recently.
        const pick = await pickNext('call-sheet');
        const n = pick && pick !== 'done' ? pick.number : latestPlayed();
        if (n === null) return { state: 'none', number: null };
        navigate(`/call-sheet/${n}`, { replace: true });
        return null;
      }
      // Preview mode (development only) starts finished: three skips is the one
      // way the server gives the answer, and the game is then shown won on the
      // second guess with the answer as the right one.
      if (PREVIEW) {
        const ended = await play(wanted, [{ skipped: true }, { skipped: true }, { skipped: true }]);
        if (!ended?.answer) return { state: 'none', number: wanted };
        const guesses: Guess[] = [{ skipped: true }, { id: ended.answer.id, kind: ended.answer.kind }];
        const won = await play(wanted, guesses);
        return won ? { state: 'ready', play: won, guesses } : { state: 'none', number: wanted };
      }
      const guesses = replay ? [] : loadProgress(wanted)?.guesses ?? [];
      const verdict = await play(wanted, guesses);
      if (!verdict) return { state: 'none', number: wanted, withdrawn: await wasWithdrawn('call-sheet', wanted) };
      return { state: 'ready', play: verdict, guesses };
    })().then(
      (next) => { if (live && next) setLoaded(next); },
      (err: Error) => { if (live) setLoaded({ state: 'error', message: err.message }); }
    );
    return () => { live = false; };
  }, [wanted, badAddress, replay, navigate]);

  if (badAddress) return <Notice message="That page doesn't exist." />;
  if (loaded.state === 'loading') return <main className="column cs-game" aria-busy="true" />;
  if (loaded.state === 'error') {
    return <Notice game="call-sheet" message={`Something went wrong loading the puzzle. ${loaded.message}`} />;
  }
  if (loaded.state === 'none') {
    return loaded.number === null
      ? <Notice game="call-sheet" message="Call Sheet starts soon. There are no puzzles to play yet." />
      : loaded.withdrawn
        ? <Notice game="call-sheet" message={`Call Sheet #${loaded.number} is no longer available.`} />
        : <Notice game="call-sheet" message={`There is no Call Sheet #${loaded.number} yet.`} />;
  }
  return <CallSheetGame key={`${loaded.play.number}${replay ? '-replay' : ''}`} initial={loaded.play}
    initialGuesses={loaded.guesses} replay={replay} />;
}

function CallSheetGame({ initial, initialGuesses, replay }: { initial: CallSheetPlay; initialGuesses: Guess[]; replay: boolean }) {
  const list = useTitles();
  const [state, setState] = useState(initial);
  const [guesses, setGuesses] = useState<Guess[]>(initialGuesses);
  const [problem, setProblem] = useState<string | null>(null);
  const submitting = useRef(false);
  const ask = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (PREVIEW || replay || guesses.length === 0) return;
    saveProgress(state.number, guesses);
  }, [state.number, guesses, replay]);

  // Finished is recorded once the server says so, which is what takes this
  // puzzle out of "the next one not played" — and the result is kept here and
  // posted (lib/history.ts). Only a first completion is ever recorded, so a
  // Call Sheet always counts.
  useEffect(() => {
    if (PREVIEW || !state.finished) return;
    // A finished replay changes nothing: it is not marked and not recorded.
    if (replay) return;
    markPlayed(state.number);
    recordFinished('call-sheet', state.number, guesses, state.won ? guesses.length : null, true);
  }, [state.finished, state.won, state.number, guesses, replay]);

  // One path for a title and a skip, because a skip is a guess: it spends one,
  // it reveals the next actor, and it counts as a miss wherever the outcome is
  // counted. Only a verdict spends the guess: a failed request leaves the game
  // as it was, to be tried again.
  async function record(g: Guess) {
    if (state.finished || submitting.current) return;
    submitting.current = true;
    setProblem(null);
    const next = [...guesses, g];
    try {
      const verdict = await play(state.number, next);
      if (verdict) { setGuesses(next); setState(verdict); }
    } catch {
      setProblem("Couldn't check that guess. Check your connection and try again.");
    } finally {
      submitting.current = false;
    }
  }

  // Finishing stays here, and the result screen opens at its top.
  useResultFromTop(state.finished);

  if (state.finished && state.answer) {
    return <Result play={{ ...state, answer: state.answer }} guesses={guesses} list={list ?? []} replay={replay} />;
  }

  const remaining = MAX_GUESSES - guesses.length;

  return (
    // The faces are spaced from this puzzle's own four names (CallSheet.css).
    <main className="column cs-game"
          style={{ '--cs-name-em': state.nameEm ?? NAME_EM_FALLBACK } as React.CSSProperties}>
      <GameHeader game="Call Sheet" number={state.number} inBar />

      {/* The line the game is carried through: the instruction now, the result
          when it is over, in the same place and at the same size. */}
      <p className="panel-line cs-line">Name the movie or show.</p>

      {/* Only the revealed actors exist here at all: the server sent no others.
          MAX_ACTORS slots, so all four are drawn from the first guess. */}
      <ActorTiles revealed={state.actors} slots={MAX_ACTORS} />

      {/* The guess area, wrapped because it is one thing to the keyboard: what
          has to stay above it is the input, Submit, whatever suggestions are
          showing and the counter — not the input alone, which is all the
          browser would think to keep (src/lib/keyboard.ts). */}
      <div className="cs-ask" ref={ask}>
        <TitleInput
          list={list ?? []}
          disabled={state.finished}
          region={ask}
          onGuess={(title: TitleRecord) => record({ id: title.id, kind: title.kind })}
          onSkip={() => record({ skipped: true })}
        />

        {problem && <p className="caption cs-remaining" role="alert">{problem}</p>}
        {/* Plural at every value, centred under the pair, as a caption. */}
        <p className="caption cs-remaining">Guesses remaining: {remaining}</p>
      </div>

      {guesses.length > 0 && (
        <GuessList guesses={guesses} results={state.results} list={list ?? []} heading="Guesses" />
      )}
    </main>
  );
}

// Every guess, the correct one included: wrong ones grey, the right one in the
// accent (CLAUDE.md: Call Sheet's "Guesses" section lists every guess). Shared
// with the result screen, because it is the same list either side of the last
// guess — which is the whole idea of the screen not changing when a game ends.
// Right or wrong is the server's word (`results`), never worked out here.
export function GuessList(
  { guesses, results, list, answer, heading }:
  { guesses: Guess[]; results: boolean[]; list: TitleRecord[]; answer?: TitleRecord | CallSheetAnswer | null; heading: string }
) {
  const answerRecord = answer ? { id: answer.id, kind: answer.kind, title: answer.title, year: answer.year } : null;
  return (
    <section className="cs-guesses">
      <h2 className="heading">{heading}</h2>
      {guesses.map((g, i) => {
        const skipped = isSkip(g);
        const right = results[i] === true;
        const title = skipped ? null : byId(g.id, g.kind, list, answerRecord);
        return (
          <div className={`cs-guess${right ? ' is-right' : ''}`} key={i}>
            <span className="cs-guess-dot" aria-hidden="true" />
            {/* Always the title's own display name, never what was typed — and
                "Skipped" where there was no title to name. A skip is shown like
                a wrong guess, because that is what it is. */}
            <span className="cs-guess-title">
              {skipped ? 'Skipped' : title?.title ?? (list.length ? 'Title no longer available' : '…')}
            </span>
            {/* The year and Movie or Show, set exactly as the dropdown sets
                them (owner, 13 October): the row says which version was
                guessed, not just which name. */}
            {title && (
              <span className="cs-suggestion-meta cs-guess-meta">
                {title.year ?? ''}
                <span className="cs-suggestion-kind">{kindLabel(title.kind)}</span>
              </span>
            )}
            <span className="visually-hidden">{right ? ' — correct' : ' — wrong'}</span>
          </div>
        );
      })}
    </section>
  );
}
